<template>
  <ll-form
    ref="formRef"
    :form-columns="formColumns"
    :form-data="props.value"
    :form-options="formOptions"
  >
    <template #formItem="{ item }">
      <template v-if="item.key === 'other'">
        <div :class="bem.e('item')">
          <div v-if="props.keyValueType !== 'single'" :class="bem.m('left')">
            <ll-form-item
              :label="attrs['key-label']"
              :label-col="{
                span: 24,
              }"
              :rules="getKeyType.rules || []"
              name="key"
            >
              <slot name="key">
                <ll-select
                  v-if="!isEmpty(props.keyTypes)"
                  v-model:value="innerValue['key']"
                  :options="props.keyTypes"
                  :disabled="getKeyDisabled"
                  style="width: 100%"
                  placeholder="请选择"
                  allow-clear
                  @change="handleChange"
                />
                <component
                  :is="`ll-${getKeyType.type}`"
                  v-else
                  v-model:value="innerValue['key']"
                  v-bind="getKeyType"
                  :disabled="getKeyDisabled"
                  style="width: 100%"
                >
                  <template
                    v-for="_item in Object.keys($slots)"
                    :key="_item"
                    #[_item]="data"
                  >
                    <slot :name="_item" v-bind="data || {}" content="key" />
                  </template>
                </component>
              </slot>
            </ll-form-item>
          </div>

          <div
            v-if="props.keyValueType !== 'single'"
            :class="bem.m('separator')"
            :style="{
              'margin-top':
                !!attrs['key-label'] || !!attrs['value-label'] ? '26px' : '0px',
            }"
          >
            <slot name="separator">
              <ll-icon style="font-size: 16px" icon-name="icon-symbol" />
            </slot>
          </div>

          <div :class="bem.m('right')">
            <ll-form-item
              :label="attrs['value-label']"
              :label-col="{
                span: 24,
              }"
              :rules="getValueType.rules || []"
              name="value"
            >
              <slot name="value-wrapper" v-bind="getValueType">
                <component
                  :is="`ll-${getValueType.type}`"
                  v-model:value="innerValue['value']"
                  :disabled="getValueDisabled"
                  style="width: 100%"
                  v-bind="getValueType"
                >
                  <template
                    v-for="_item in Object.keys($slots)"
                    :key="_item"
                    #[_item]="data"
                  >
                    <slot
                      :name="_item"
                      v-bind="data || {}"
                      content="value"
                    ></slot>
                  </template>
                </component>
              </slot>
            </ll-form-item>
          </div>
          <template v-if="hasValue">
            <a-popconfirm
              title="确定要移除吗?"
              ok-text="确认"
              cancel-text="取消"
              placement="topRight"
              :overlay-class-name="bem.m('popconfirm')"
              :get-popup-container="(trigger: Element) => trigger.parentNode"
              destroy-tooltip-on-hide
              @confirm="handleDelete"
            >
              <div
                :class="bem.m('delete')"
                :style="{
                  'margin-top':
                    !!attrs['key-label'] || !!attrs['value-label']
                      ? '26px'
                      : '0px',
                }"
              >
                <ll-button type="link" :disabled="getDeleteDisabled">
                  <slot name="delete-icon">
                    <ll-icon icon-name="icon-remove" class="delete" />
                  </slot>
                </ll-button>
              </div>
            </a-popconfirm>
          </template>
          <div
            v-else
            :class="bem.m('delete')"
            :style="{
              'margin-top':
                !!attrs['key-label'] || !!attrs['value-label'] ? '26px' : '0px',
            }"
          >
            <ll-button
              type="link"
              :disabled="getDeleteDisabled"
              @click="handleDelete"
            >
              <slot name="delete-icon">
                <ll-icon icon-name="icon-remove" class="delete" />
              </slot>
            </ll-button>
          </div>
        </div>
      </template>
    </template>
  </ll-form>
</template>

<script setup lang="ts">
import { ref, computed, useAttrs, watch, nextTick } from 'vue'
import { isEmpty } from 'lodash-es'
import { createNamespace } from '@ll-plus/utils'
import type { FormInstance } from '@ll-plus/components'
import {
  keyValueItemProps,
  keyValueItemEmits,
  formColumns,
  formOptions,
} from '../config'
import { type FormColumn } from '@ll-plus/components'
import type { NamePath } from 'ant-design-vue/es/form/interface'

defineOptions({
  name: 'LlKeyValueItem',
})

const props = defineProps(keyValueItemProps)
const emits = defineEmits(keyValueItemEmits)

const attrs = useAttrs()
const bem = createNamespace('key-value')
const innerValue = ref(props.value)

const formRef = ref<FormInstance | null>()

const getKeyType = computed<FormColumn>(() => {
  const option = props.keyType
  return option
})
const getValueType = computed<FormColumn>(() => {
  let option = props.valueType
  if (innerValue.value['key'] && !isEmpty(props.keyValueTypeDict)) {
    option = props.keyValueTypeDict[innerValue.value['key']]
  }
  return option
})

const getKeyDisabled = computed(() => {
  return (
    props.disabledKey?.includes(props.idx) ||
    props.disabled ||
    getKeyType.value?.disabled
  )
})

const getValueDisabled = computed(() => {
  return (
    props.disabledValue?.includes(props.idx) ||
    props.disabled ||
    getValueType.value?.disabled
  )
})

const hasValue = computed(() => {
  return (
    innerValue.value['key'] ||
    innerValue.value['key'] === 0 ||
    innerValue.value['value']
  )
})

const getDeleteDisabled = computed(() => {
  return (
    props.disabledDelete?.includes(props.idx) ||
    props.disabled ||
    (props.values.length === 1 && !hasValue.value)
  )
})

const handleDelete = () => {
  emits('delete')
}

const handleChange = async () => {
  innerValue.value['value'] = null
  await formRef.value?.clearValidate()
}

const validate = async () => {
  await nextTick()
  try {
    await formRef.value?.validate()
    return Promise.resolve()
  } catch (error) {
    console.log({ error }, props.idx)
    return Promise.reject()
  }
}

const resetFields = async () => {
  await formRef.value?.resetFields()
}

const clearValidate = async (namePath?: NamePath[]) => {
  await formRef.value?.clearValidate(namePath)
}

watch(
  innerValue,
  async (val) => {
    await nextTick().then(() => {
      try {
        emits('update:value', val)
        emits('change')
      } catch (error) {
        console.error(error)
      }
    })
  },
  {
    deep: true,
  }
)

defineExpose({
  validate,
  resetFields,
  clearValidate,
})
</script>

<style scoped></style>
